import React, {useRef, useState} from "react";

const MIN_HOLD_MS = 500;

// Hold-to-talk voice input: records while held, then uploads for Whisper +
// LLM interpretation. onResult receives {transcript, answered, value} —
// answered=false means filler/silence, so the caller can re-ask. Holds
// shorter than MIN_HOLD_MS are discarded (accidental taps, and the
// first-use mic-permission prompt eating the press).
export const HoldToTalk = ({expect = 'free', context = null, onResult, label = 'Hold to Speak', uploadTo = '/voice/interpret', variant = 'gold', disabled = false}) => {
    const [state, setState] = useState('idle'); // idle | recording | processing
    const [hint, setHint] = useState(null);
    const recorderRef = useRef(null);
    const heldRef = useRef(false);
    const startedAtRef = useRef(0);
    const discardRef = useRef(false);

    const showHint = (text) => {
        setHint(text);
        setTimeout(() => setHint(null), 1600);
    };

    const start = async (e) => {
        e.preventDefault();
        if (disabled || state !== 'idle') return;
        heldRef.current = true;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({audio: true});
            // The permission prompt (or a slow mic) may outlive the press —
            // if the finger is already up, don't start a phantom recording.
            if (!heldRef.current) {
                stream.getTracks().forEach(t => t.stop());
                return;
            }
            const mime = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm';
            const recorder = new MediaRecorder(stream, {mimeType: mime});
            const chunks = [];
            recorder.ondataavailable = (ev) => { if (ev.data.size) chunks.push(ev.data); };
            recorder.onstop = async () => {
                stream.getTracks().forEach(t => t.stop());
                recorderRef.current = null;
                if (discardRef.current) {
                    discardRef.current = false;
                    setState('idle');
                    return;
                }
                setState('processing');
                const blob = new Blob(chunks, {type: mime});
                const form = new FormData();
                form.append('audio', blob, mime === 'audio/mp4' ? 'speech.m4a' : 'speech.webm');
                form.append('expect', expect);
                if (context) form.append('context', typeof context === 'string' ? context : JSON.stringify(context));
                try {
                    const res = await fetch(uploadTo, {method: 'POST', credentials: 'include', body: form});
                    onResult(await res.json());
                } catch (err) {
                    onResult({transcript: '', answered: false, value: '', error: true});
                }
                setState('idle');
            };
            recorder.start();
            recorderRef.current = recorder;
            startedAtRef.current = Date.now();
            setState('recording');
        } catch (err) {
            heldRef.current = false;
            onResult({transcript: '', answered: false, value: '', error: true, micDenied: true});
        }
    };

    const stop = (e) => {
        e.preventDefault();
        heldRef.current = false;
        const r = recorderRef.current;
        if (!r || r.state !== 'recording') return;
        if (Date.now() - startedAtRef.current < MIN_HOLD_MS) {
            discardRef.current = true;
            showHint('Hold it down while you speak');
        }
        r.stop();
    };

    const recording = state === 'recording';
    const evil = variant === 'evil';
    const locked = disabled || state === 'processing';
    return <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: evil ? 26 : 14}}>
        <button
            onPointerDown={start}
            onPointerUp={stop}
            onPointerLeave={stop}
            onContextMenu={(e) => e.preventDefault()}
            disabled={disabled || state === 'processing'}
            style={{
                width: 200,
                height: 200,
                borderRadius: '50%',
                opacity: locked ? 0.55 : 1,
                transition: 'opacity 400ms, background 400ms, box-shadow 400ms',
                border: evil ? 'none' : (recording ? '5px solid #ff5a3a' : '5px solid #e8b15a'),
                background: evil
                    ? (locked ? '#4a4a4a' : recording ? '#ff1f0f' : '#c41E0f')
                    : (recording
                        ? 'radial-gradient(circle, #5a1510 0%, #2a0b08 100%)'
                        : 'linear-gradient(180deg, #2a2520 0%, #14110d 100%)'),
                color: evil ? 'transparent' : (recording ? '#ff8a72' : '#f1d28b'),
                fontSize: 22,
                fontWeight: 900,
                fontFamily: 'inherit',
                letterSpacing: 2,
                textTransform: 'uppercase',
                cursor: 'pointer',
                touchAction: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none',
                boxShadow: evil
                    ? (locked ? 'none' : recording ? '0 0 80px rgba(255,40,20,0.9)' : '0 0 34px rgba(255,40,20,0.55)')
                    : (recording
                        ? '0 0 60px rgba(255,90,60,0.7)'
                        : '0 0 22px rgba(232,177,90,0.5), 4px 4px 0 rgba(0,0,0,0.7)'),
            }}
        >
            {evil ? '' : (state === 'processing' ? '…' : recording ? '● Listening' : label)}
        </button>
        <div style={{fontSize: evil ? 21 : 15, fontWeight: 800, letterSpacing: evil ? 4 : 3, textTransform: 'uppercase', color: evil ? '#ff9d8a' : '#ff8a72', minHeight: 26, textShadow: '1px 1px 0 rgba(0,0,0,0.9)'}}>
            {hint || (evil ? (recording ? 'Listening' : (disabled || state === 'processing') ? 'Wait...' : 'Hold to Talk') : '')}
        </div>
    </div>;
};
